import React, { useEffect, useState } from 'react';
import { Logger } from './utils';
import { useTwilioAppState } from './state';
import { withRouter } from 'react-router-dom';
import AllowPermissions from './components/AllowPermissions';
import Controls from './components/Controls/Controls';
import MiniSpinner from '../../common/MiniSpinner';
import Room from './components/Room';
import Spinner from '../../common/spinner';
import useDeviceSettingsToggle from './hooks/useDeviceSettingsToggle';
import useIsLocalParticipantReconnecting from './hooks/useIsLocalParticipantReconnecting';
import useIsRemoteParticipantReconnecting from './hooks/useIsRemoteParticipantReconnecting';
import useMainSpeaker from './hooks/useMainSpeaker';
import useParticipants from './hooks/useParticipants';
import usePublications from './hooks/usePublications';
import useRoomState from './hooks/useRoomState';
import useScreenShareParticipant from './hooks/useScreenShareParticipant';
import useShowControls from './hooks/useShowControls';
import useVideoContext from './hooks/useVideoContext';
import WaitingRoom from './components/WaitingRoom';

function TwilioVideoApp(props) {
    const roomState = useRoomState();
    const {
        connect,
        getAudioAndVideoTracks,
        isAcquiringLocalTracks,
        isAcquiringLocalTracksCompleted,
        isConnecting,
        room,
    } = useVideoContext();
    const {
        isFetching,
        isFromNonLogin,
        isPermissionToDevices,
        roomName,
        twilioToken,
    } = useTwilioAppState();
    const { showWaitingMsg } = useParticipants();
    const [isEndCall, setIsEndCall] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [showControls, setShowControls] = useShowControls();
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isRoomConnectComplete, setIsRoomConnectComplete] = useState(false);
    const localParticipantReconnecting = useIsLocalParticipantReconnecting();
    const remoteParticipantReconnecting = useIsRemoteParticipantReconnecting();
    const roomDisconnected = roomState && roomState === 'disconnected';
    const roomConnected =
        roomState === 'connected' ||
        (remoteParticipantReconnecting && !roomDisconnected);
    const mainParticipant = useMainSpeaker();
    let showVideoBackground = false;
    const publications = usePublications(mainParticipant);
    const videoOrScreenPublication =
        publications.find(p => p.trackName.includes('camera')) ||
        publications.find(p => p.trackName.includes('screen'));
    showVideoBackground =
        videoOrScreenPublication && mainParticipant.state === 'connected'
            ? true
            : false;
    const [deviceSettingsOpen, toggleDeviceSettings] = useDeviceSettingsToggle();
    const { enableVirtualWaitingRoom } = window.serverData.data;
    const noBorderClass =
        roomConnected && showWaitingMsg && enableVirtualWaitingRoom;
    let showBackdrop = deviceSettingsOpen ? true : false;

    if (roomState === 'reconnecting') showVideoBackground = false;

    const screenShareParticipant = useScreenShareParticipant();
    const screenShareParticipantName =
        (screenShareParticipant &&
            screenShareParticipant.identity &&
            screenShareParticipant.identity.split('||') &&
            screenShareParticipant.identity.split('||').length >= 3 &&
            screenShareParticipant.identity.split('||')[3]) ||
        '';

    const onDrawerStateChange = (isDrawerOpen, fullScreenEnabled) => {
        setShowOptions(isDrawerOpen);
        setIsFullScreen(fullScreenEnabled);
    };

    const onBackdropClick = () => {
        Logger(`UI.Twilio.Room.Backdrop.Clicked`, new Date());
        if (deviceSettingsOpen) {
            toggleDeviceSettings(false);
        }
    };

    useEffect(() => {
        if (props.error) {
            if (props.onTwilioRoomError) props.onTwilioRoomError(props.error);
        }
    }, [props.error]);

    useEffect(() => {
        if (
            twilioToken &&
            roomName &&
            isPermissionToDevices &&
            !isAcquiringLocalTracks &&
            !isAcquiringLocalTracksCompleted &&
            !isRoomConnectComplete &&
            !roomConnected
        ) {
            setIsRoomConnectComplete(true);
            getAudioAndVideoTracks()
                .then(tracks => {
                    if (tracks && typeof tracks === 'string') {
                        return;
                    } //Avoid race condition. Tracks Already Acquired
                    Logger(
                        `UI.Twilio.Room.onTrackFetch.${tracks && tracks.length ? 'Success' : 'TrackNotFound'
                        }`,
                        new Date(),
                        { tracks }
                    );
                    connect(
                        twilioToken,
                        roomName,
                        tracks && tracks.length ? tracks : null
                    ).then(rsp => {
                        if (rsp.isSuccess) {
                            Logger('UI.Twilio.Room.onRoomConnectSuccess', new Date(), {
                                rsp,
                            });
                        } else {
                            Logger('UI.Twilio.Room.onRoomConnectFailure', new Date(), {
                                rsp,
                            });
                            if (props.onTwilioRoomError) props.onTwilioRoomError(rsp.error);
                        }
                    });
                })
                .catch(err => {
                    Logger(
                        'UI.Twilio.Room.onTrackFetchFailure',
                        new Date(),
                        JSON.stringify(err)
                    );
                    if (props.onTwilioRoomError) props.onTwilioRoomError(err);
                });
        }
    }, [twilioToken, roomName, isAcquiringLocalTracks, getAudioAndVideoTracks]);

    useEffect(() => {
        let didCancel = false;
        if (!didCancel) {
            if (
                screenShareParticipant &&
                screenShareParticipant !== room.localParticipant
            ) {
                setShowControls(false);
            }
        }
        return () => {
            didCancel = true;
        };
    }, [screenShareParticipant]);

    return (
        <div
            className={
                !isFromNonLogin
                    ? 'container-fluid body-content twilio-video-container'
                    : ''
            }
        >
            {isEndCall && roomConnected ? (
                <div className='row'>
                    <div className='col-12'>
                        <Spinner />
                    </div>
                </div>
            ) : (
                <>
                    {roomState === 'connected' && showWaitingMsg && props.waitingRoomMsg && (
                        <div className='row waiting-room-msg-container'>
                            <div className='col-12 text-center maxWidth-content'>
                                <h2>{props.waitingRoomMsg}</h2>
                            </div>
                        </div>
                    )}

                    <div className='row'>
                        <div className='col-12 maxWidth-content'>
                            <>
                                {roomConnected && showWaitingMsg && enableVirtualWaitingRoom ? (
                                    <WaitingRoom />
                                ) : (
                                    <div
                                        className={
                                            noBorderClass
                                                ? 'main-container no-border'
                                                : showVideoBackground
                                                    ? 'main-container main-participant-container'
                                                    : 'main-container'
                                        }
                                        onMouseOver={() => setShowControls(true)}
                                        onTouchEnd={() => setShowControls(true)}
                                        onMouseOut={() => setShowControls(false)}
                                    >
                                        {(!isPermissionToDevices ||
                                            isFetching ||
                                            (localParticipantReconnecting && !roomDisconnected)) && (
                                                <div className='row token-fetching-loader'>
                                                    <div className='col-12 align-self-center'>
                                                        {localParticipantReconnecting &&
                                                            !roomDisconnected && (
                                                                <div className='msg-reconnecting'>
                                                                    Something went wrong. We're trying to reconnect
                                                                    you.
                                                                </div>
                                                            )}
                                                        <MiniSpinner className='videovisit-mini-spinner' />
                                                    </div>
                                                </div>
                                            )}
                                        {(isAcquiringLocalTracks || isConnecting) && (
                                            <AllowPermissions />
                                        )}
                                        {roomConnected && (
                                            <div
                                                className={`${!(showWaitingMsg && enableVirtualWaitingRoom) &&
                                                        (showControls || showBackdrop)
                                                        ? 'show-controls'
                                                        : 'hide-controls'
                                                    } ${screenShareParticipant ? 'screen-share-view' : ''
                                                    } ${showBackdrop ? 'backdrop-enabled' : ''} ${showOptions ? ' drawer-open' : ''
                                                    } ${isFullScreen ? ' full-screen-view' : ''}`}
                                            >
                                                {showWaitingMsg && enableVirtualWaitingRoom ? (
                                                    ''
                                                ) : (
                                                    <Room />
                                                )}

                                                {screenShareParticipant && (
                                                    <div className='screen-share-banner'>
                                                        <div className='screen-share-text'>
                                                            {screenShareParticipant === room.localParticipant
                                                                ? "You're sharing your screen"
                                                                : `${screenShareParticipantName} is presenting`}
                                                        </div>
                                                    </div>
                                                )}

                                                <Controls
                                                    setIsEndCall={setIsEndCall}
                                                    showWaitingMsg={
                                                        roomState === 'connected' && showWaitingMsg
                                                    }
                                                    deviceSettingsOpen={deviceSettingsOpen}
                                                    toggleDeviceSettings={toggleDeviceSettings}
                                                    isFullScreen={isFullScreen}
                                                    onDrawerStateChange={onDrawerStateChange}
                                                />

                                                {showBackdrop && (
                                                    <div
                                                        className='twilio-video-backdrop'
                                                        onClick={() => onBackdropClick()}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default withRouter(TwilioVideoApp);
